// BlogPage.js
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, Search, Filter, ArrowLeft } from 'lucide-react';

const BlogPage = () => {
  const blogPosts = [
    {
      id: 1,
      date: 'March 10, 2024',
      title: 'A Practical Guide to Studying Abroad: From Research to Admission',
      excerpt: 'Choosing the right country, university, and course can feel overwhelming. This guide breaks down the key steps involved in studying abroad, from initial research and consultations to preparing documents and submitting strong applications.',
      image: 'blog-1.jpg',
      link: '/blog/practical-guide-studying-abroad',
      readTime: '8 min read',
      category: 'Study Abroad'
    },
    {
      id: 2,
      date: 'July 18, 2024',
      title: 'Working Abroad: How to Prepare for International Job Opportunities',
      excerpt: 'Working abroad requires more than finding an open role. Learn how to prepare for overseas employment, understand employer expectations, get interview ready, and organise the documentation needed to pursue work opportunities abroad with confidence.',
      image: 'blog-2.jpg',
      link: '/blog/working-abroad-preparation',
      readTime: '10 min read',
      category: 'Work Abroad'
    },
    {
      id: 3,
      date: 'July 24, 2024',
      title: 'Making Informed Decisions About Study and Work Abroad',
      excerpt: 'Studying or working abroad is a major life decision. This article explores important factors to consider before starting your journey, including eligibility, timelines, costs, and how professional guidance can help you avoid common mistakes.',
      image: 'blog-3.jpg',
      link: '/blog/informed-decisions-study-work-abroad',
      readTime: '12 min read',
      category: 'Decision Making'
    }
  ];

  const categories = ['All', 'Study Abroad', 'Work Abroad', 'Decision Making'];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#202356] to-[#2542D4]">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl md:text-5xl font-semibold mb-6 leading-tight">
              Blog & Resources
            </h1>
            <p className="text-xl font-light leading-relaxed text-white/90 max-w-3xl mx-auto">
              Practical insights, updates, and guidance on studying and working abroad. Our articles cover global education trends, international job markets, and application preparation to help you make informed decisions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Category Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 text-gray-600">
                <Filter size={18} />
                <span className="font-medium">Filter by category:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.map((category, index) => (
                  <button
                    key={index}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                      index === 0
                        ? 'bg-[#202356] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Blog Posts Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link to={post.link} className="group block h-full">
                  <article className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 h-full flex flex-col">
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <div 
                        className="absolute inset-0 bg-cover bg-center transform group-hover:scale-105 transition-transform duration-500"
                        style={{ 
                          backgroundImage: `url(${require(`../assets/${post.image}`)})`,
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                      
                      {/* Category Badge */}
                      <div className="absolute top-4 left-4">
                        <span className="bg-white/90 backdrop-blur-sm text-[#202356] text-xs font-medium px-3 py-1 rounded-full">
                          {post.category}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 flex flex-col flex-grow">
                      {/* Meta */}
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                        <div className="flex items-center gap-1">
                          <Calendar size={14} />
                          <span>{post.date}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock size={14} />
                          <span>{post.readTime}</span>
                        </div>
                      </div>

                      {/* Title */}
                      <h3 className="text-xl font-semibold text-[#202356] mb-3 leading-tight group-hover:text-[#2d3270] transition">
                        {post.title}
                      </h3>

                      {/* Excerpt */}
                      <p className="text-gray-600 text-sm leading-relaxed mb-6 flex-grow">
                        {post.excerpt}
                      </p>

                      {/* Read More */}
                      <div className="flex items-center gap-2 text-[#202356] text-sm font-medium group-hover:gap-3 transition-all">
                        <span>Read Article</span>
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </article>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Newsletter Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-20"
          >
            <div className="bg-gradient-to-r from-[#202356] to-[#2542D4] rounded-2xl p-8 md:p-12 text-center text-white">
              <h2 className="text-3xl font-semibold mb-4">
                Stay Updated with Latest Insights
              </h2>
              <p className="text-white/90 mb-8 max-w-2xl mx-auto">
                Get practical tips and guidance delivered to your inbox. Join our community of aspiring international students and professionals.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-grow px-4 py-3 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50"
                />
                <button className="bg-white text-[#202356] px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition">
                  Subscribe
                </button>
              </div>
            </div>
          </motion.div>

          {/* Back to Home */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 text-center"
          >
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-[#202356] hover:text-[#2d3270] transition font-medium"
            >
              <ArrowLeft size={20} />
              Back to Home
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
